import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageStates";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { wishes, child, ledger, formatDate } from "@/lib/mock-data";

export const Route = createFileRoute("/app/wishes/$id/redeem")({
  component: RedeemConfirm,
});

function RedeemConfirm() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const wish = wishes.find((w) => w.id === id);
  const [execNote, setExecNote] = useState("");
  const [parentNote, setParentNote] = useState("");
  const [feeling, setFeeling] = useState("");

  if (!wish) return null;

  // pick soonest-expiring earn entries
  const consume = ledger
    .filter((l) => l.kind === "earn")
    .sort((a, b) => (a.expiresAt || "").localeCompare(b.expiresAt || ""));
  let remaining = wish.required;
  const consumed: typeof consume = [];
  for (const l of consume) {
    if (remaining <= 0) break;
    consumed.push(l);
    remaining -= l.delta;
  }

  return (
    <div className="pb-24">
      <PageHeader
        title="确认兑换"
        onBack={() => navigate({ to: "/app/wishes/$id", params: { id } })}
      />
      <div className="space-y-4 px-4 pt-4">
        <section className="rounded-[var(--r-lg)] border bg-surface p-4">
          <p className="text-[var(--fs-lg)] font-semibold">{wish.title}</p>
          <div className="mt-2 flex items-center justify-between text-[var(--fs-sm)]">
            <span style={{ color: "var(--muted)" }}>所需点数</span>
            <span className="font-semibold">{wish.required}</span>
          </div>
          <div className="mt-1 flex items-center justify-between text-[var(--fs-sm)]">
            <span style={{ color: "var(--muted)" }}>当前可用</span>
            <span>{child.availablePoints}</span>
          </div>
          <div
            className="mt-2 flex items-center justify-between border-t pt-2 text-[var(--fs-sm)]"
          >
            <span style={{ color: "var(--muted)" }}>兑换后剩余</span>
            <span className="font-semibold" style={{ color: "var(--accent)" }}>
              {child.availablePoints - wish.required}
            </span>
          </div>
        </section>

        <section>
          <h3 className="mb-2 px-1 text-[var(--fs-sm)] font-semibold">
            将扣除的点数(按最早过期优先)
          </h3>
          <ul className="space-y-2">
            {consumed.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between rounded-[var(--r-md)] border bg-surface p-3 text-[var(--fs-sm)]"
              >
                <div>
                  <p>{l.source}</p>
                  <p
                    className="text-[var(--fs-xs)]"
                    style={{ color: "var(--muted)" }}
                  >
                    过期 {l.expiresAt ? formatDate(l.expiresAt) : "—"}
                  </p>
                </div>
                <span style={{ color: "var(--danger)" }}>−{l.delta}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-3">
          <div>
            <Label className="mb-1">执行备注(可选)</Label>
            <Textarea
              value={execNote}
              onChange={(e) => setExecNote(e.target.value)}
              placeholder="例如:周六下午 3 点带他去"
              className="min-h-[60px]"
            />
          </div>
          <div>
            <Label className="mb-1">家长评语(可选)</Label>
            <Textarea
              value={parentNote}
              onChange={(e) => setParentNote(e.target.value)}
              className="min-h-[60px]"
            />
          </div>
          <div>
            <Label className="mb-1">孩子感受(可选)</Label>
            <Textarea
              value={feeling}
              onChange={(e) => setFeeling(e.target.value)}
              className="min-h-[60px]"
            />
          </div>
        </section>
      </div>

      <div className="sticky bottom-0 border-t bg-surface/95 px-4 py-3 safe-bottom backdrop-blur">
        <Button
          className="h-12 w-full text-base"
          onClick={() => {
            toast.success("兑换成功");
            navigate({ to: "/app/wishes" });
          }}
        >
          确认兑换
        </Button>
      </div>
    </div>
  );
}
